import axios from 'axios';
import { getAuthHeader } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// CSRF token management for size chart service
let csrfToken = null;

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Get all size charts
export const getSizeCharts = async () => {
  try {
    const { data } = await axios.get(`${API_URL}/api/admin/size-charts`, {
      headers: getAuthHeader(),
      withCredentials: true
    });
    return data;
  } catch (error) {
    console.error('Error fetching size charts:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch size charts'
    };
  }
};

// Get a single size chart by ID
export const getSizeChart = async (id) => {
  try {
    const { data } = await axios.get(`${API_URL}/api/admin/size-charts/${id}`, {
      headers: getAuthHeader(),
      withCredentials: true
    });
    return data;
  } catch (error) {
    console.error('Error fetching size chart:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch size chart'
    };
  }
};

// Create a new size chart
export const createSizeChart = async (formData) => {
  try {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    const { data } = await axios.post(`${API_URL}/api/admin/size-charts`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
        ...(csrfToken && { 'x-csrf-token': csrfToken })
      },
      withCredentials: true
    });
    return data;
  } catch (error) {
    console.error('Error creating size chart:', error);
    // If CSRF error, clear token and retry
    if (error.response?.status === 403 && error.response?.data?.error === 'CSRF token validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        return createSizeChart(formData);
      }
    }
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to create size chart'
    };
  }
};

// Update a size chart
export const updateSizeChart = async (id, formData) => {
  try {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    const { data } = await axios.put(`${API_URL}/api/admin/size-charts/${id}`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
        ...(csrfToken && { 'x-csrf-token': csrfToken })
      },
      withCredentials: true
    });
    return data;
  } catch (error) {
    console.error('Error updating size chart:', error);
    // If CSRF error, clear token and retry
    if (error.response?.status === 403 && error.response?.data?.error === 'CSRF token validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        return updateSizeChart(id, formData);
      }
    }
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to update size chart'
    };
  }
};

// Delete a size chart
export const deleteSizeChart = async (id) => {
  try {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    const { data } = await axios.delete(`${API_URL}/api/admin/size-charts/${id}`, {
      headers: {
        ...getAuthHeader(),
        ...(csrfToken && { 'x-csrf-token': csrfToken })
      },
      withCredentials: true
    });
    return data;
  } catch (error) {
    console.error('Error deleting size chart:', error);
    // If CSRF error, clear token and retry
    if (error.response?.status === 403 && error.response?.data?.error === 'CSRF token validation failed') {
      csrfToken = null;
      await fetchCsrfToken();
      if (csrfToken) {
        return deleteSizeChart(id);
      }
    }
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to delete size chart'
    };
  }
};

// Get size chart for a product (public)
export const getProductSizeChart = async (productSlug) => {
  try {
    const { data } = await axios.get(`/api/products/${productSlug}/size-chart`);
    return data;
  } catch (error) {
    console.error('Error fetching product size chart:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch size chart for this product'
    };
  }
}; 